// tslint:disable: max-classes-per-file
/**
 * @file
 * Weight Leader
 */

import castArray from 'lodash/castArray';
import * as React from 'react';

// Util
import * as api from '../../util/api';
import {
  prettifyOunces,
  tournamentTypeString,
} from '../../util/util';

// Components
import BassComponent from '../../components/BassComponent/BassComponent';
import Loader from '../../components/Loader/Loader';
import Message from '../../components/Message/Message';

// Higher order components
import withTournamentTypeFromParams from '../../hoc/withTournamentTypeFromParams';

// Types
import {
  IAnglerRedis,
  IAnglerTMS,
  ITournamentRedis,
  IWithTournamentType,
  RefreshRate,
  TournamentClass,
  TournamentId,
} from '../../types';

// Config
import {
  getProfileUrlById,
  getRTLPortraitById,
  SHARED_REFRESH_RATE,
} from '../../config';
import meta from './meta';

export enum WeightLeaderVariant {
  BIGBASS = 'bigbass',
  LEADERBOARD = 'leaderboard',
}

interface IProps extends IWithTournamentType {
  // The tournament id
  tid: TournamentId;
  // Should be either 'bigbass' or 'leaderboard'.
  variant: WeightLeaderVariant;
  // Optional refresh rate. Defaults to [30000, 30000].
  refreshRate?: RefreshRate;
  // NOTE: gets `type` from extends, either 1 (pro) | 0 (co).
  // type: TournamentType;
  space: TournamentClass;
}

interface IState {
  // Derived from IWeightLeaderAngler.ID
  anglerImg: string;
  // Derived from IWeightLeaderAngler.ID
  anglerLink: string;
  // Derived from IWeightLeaderAngler.NAME_FONTNAME
  anglerName: string;
  // Derived from props.tid
  componentTitle: string;
  // Derived setWeight() returns TOTALWEIGHT['@'], TODAYBIGBASS['@']
  componentWeight: number;
  // Status of data fetch. Determines how variant renders
  dataFetched: boolean;
  // Loading
  loading: boolean;
}

export class WeightLeader extends React.Component<IProps, IState> {
  public static defaultProps: Pick<IProps, 'space' | 'refreshRate'> = {
    refreshRate: SHARED_REFRESH_RATE,
    space: TournamentClass.ELITE,
  };

  /**
   *  Variant does a sponsor check by tid and displays the TODAYBIGBASS['#'] weight,
   *  defaults to 'Phoenix Boats Big Bass' if 'bigbass', OR 'Weight Leader' and
   *  TOTALWEIGHT['#'] weight if 'leaderboard'.
   */
  public static getSponsor(variant: WeightLeaderVariant, tid: TournamentId) {
    if (variant !== WeightLeaderVariant.BIGBASS) {
      return 'Weight Leader';
    }

    switch (tid) {
      // Tournament overrides for Big Bass sponsor.
      case 1181:
        return 'Toyota Big Bass';
      case 1263:
      case 1322:
        return 'Toyota Tundra Big Bass';
      case 1316:
      case 1258:
      case 1227:
      case 1178:
      case 1356:
        return 'Berkley Big Bass';
      default:
        return 'Phoenix Boats Big Bass';
    }
  }

  /**
   * Returns weight depending on variant.
   */
  public static getWeight(
    data: IAnglerRedis | IAnglerTMS,
    variant: WeightLeaderVariant
  ): number {
    // Simple lookup object to find right value per type + variant
    const weightLookup: { [v: string]: string } = {
      bigbass: data?.todays_BigFishOuncesAfterPenalty,
      leaderboard: data?.Ranking_SumTotalOuncesAfterPenalty,
    };

    return parseInt(weightLookup[variant], 10);
  }

  public state = {
    anglerImg: '',
    anglerLink: '',
    anglerName: '',
    componentTitle: '',
    componentWeight: 0,
    dataFetched: false,
    loading: true,
  };

  public setAnglerData = () => (data: ITournamentRedis) => {
    if (!data) {
      return this.setState({
        dataFetched: false,
        loading: false,
      });
    }
    const { variant, tid } = this.props;
    const anglerData = data[0];
    const anglerId = anglerData?.FishermenID;

    return this.setState({
      anglerImg: getRTLPortraitById(anglerId),
      anglerLink: getProfileUrlById(anglerId),
      anglerName: anglerData?.FontName,
      componentTitle: WeightLeader.getSponsor(variant, tid),
      componentWeight: WeightLeader.getWeight(anglerData, variant),
      dataFetched: true,
      loading: false,
    });
  };

  /**
   * Gets the API URI for the component.
   *
   * @param variant {WeightLeaderVariant enum} either 'biggbass' or 'leader
   * @param typeString {string} either 'pro' or 'co'
   * @param tid {number} Tournament ID
   * @returns
   */
  apiUri = (variant, typeString, tid) => {
    if (variant === WeightLeaderVariant.BIGBASS) {
      return `/wp-json/data/v1/tournament/leaderboard/${variant}?tournament_id=${tid}&class=${ typeString === 'pro' ? 'anglers' : 'coanglers' }`;
    } else {
      return `/wp-json/data/v1/tournament/leaderboard?tournament_id=${tid}&class=${ typeString === 'pro' ? 'anglers' : 'coanglers' }`;
    }
  }

  public async componentDidMount() {
    const { refreshRate, variant, type, tid } = this.props;
    const typeString = tournamentTypeString(type); // 'pro'|'co'

    /**
     * Hits dynamic routes based on redis key comprised of
     * Tournament ID, and component type and variant.
     * @param variant {WeightLeaderVariant enum} either 'biggbass' or 'leaderboard'
     * @param type {WeightLeaderType enum} either 'pro' or 'co'
     * @param tid { Tournament Id number }
     * @example /data/leaderboard-pro/1225
     * @example /data/leaderboard-co/1225
     * @example /data/bigbass-pro/1225
     * @example /data/bigbass-co/1225
     */
    await api.subscribe({
      callback: this.setAnglerData(),
      rate: refreshRate || SHARED_REFRESH_RATE,
      uri: this.apiUri(variant, typeString, tid),
    });
  }

  /**
   * Renders Weight Leader Variant
   */
  public render() {
    const {
      anglerImg,
      anglerLink,
      anglerName,
      componentTitle,
      componentWeight,
      dataFetched,
      loading,
    } = this.state;
    const { variant, space } = this.props;
    return (
      <BassComponent
        meta={{
          ...meta,
          ...{ nickname: `Weight Leader${variant ? `: ${variant}` : ''}` },
        }}
      >
        <div
          data-testid="WeightLeader"
          className={`${this.props.variant}--weight-leader-wrapper leaderboard-list--weight-leader-wrapper`}
        >
          <Loader loading={loading} />

          {!loading && dataFetched && (
            <div className="angler-weight-leader__wrapper">
              <img src={anglerImg} alt={anglerName} />
              <div className="angler-weight-leader__leader-profile-wrapper">
                <h3 className="angler-weight-leader__leader-title">
                  {componentTitle}
                </h3>
                <span className="angler-weight-leader__leader-name">
                  <a href={anglerLink}>{anglerName}</a>
                </span>
                <br />
                <span className="angler-weight-leader__leader-weight">
                  Weight: {prettifyOunces(componentWeight)}
                </span>
              </div>
            </div>
          )}

          <Message
            customClass="weight-leader__no-results-message"
            shouldShow={!loading && !dataFetched}
          />
        </div>
      </BassComponent>
    );
  }
}

// Pull ?pro=1|0 param off URL and pass into WeightLeader by default
export default withTournamentTypeFromParams(WeightLeader);
